// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

// ============================================================================
// "All Clubs" mode — persisted in localStorage
// ============================================================================

const ALL_CLUBS_KEY = 'clubview_all_clubs';

export function useAllClubsMode() {
  const [allClubs, setAllClubsState] = useState(() => localStorage.getItem(ALL_CLUBS_KEY) === 'true');

  const setAllClubs = useCallback((value: boolean) => {
    if (value) {
      localStorage.setItem(ALL_CLUBS_KEY, 'true');
    } else {
      localStorage.removeItem(ALL_CLUBS_KEY);
    }
    setAllClubsState(value);
  }, []);

  return { allClubs, setAllClubs };
}

// ============================================================================
// Club colors — consistent palette across all combined views
// ============================================================================

const CLUB_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#84CC16'];

export function getClubColor(index: number): string {
  return CLUB_COLORS[index % CLUB_COLORS.length];
}

// ============================================================================
// Types
// ============================================================================

export type CombinedDashboardClub = {
  club_id: string;
  club_name: string;
  member_id: string;
  roles: string[];
  joined_at: string;
  upcoming_events_count: number;
  unread_notices_count: number;
};

export type CombinedEvent = {
  id: string;
  club_id: string;
  club_name: string;
  name: string;
  start_time: string;
  end_time: string;
  location?: string;
  status: string;
};

export type CombinedTimetableEntry = {
  club_id: string;
  club_name: string;
  slot_id: string;
  slot_name: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  room_name?: string;
  instructor_name?: string;
  colour?: string;
  registration_id: string;
};

export type CombinedNotice = {
  id: string;
  club_id: string;
  club_name: string;
  type: string;
  title: string;
  body?: string;
  pinned: boolean;
  is_read: boolean;
  created_at: string;
  expires_at?: string;
};

// ============================================================================
// Hooks
// ============================================================================

export function useCombinedDashboard() {
  const [clubs, setClubs] = useState<CombinedDashboardClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ clubs: CombinedDashboardClub[] }>('/members/me/combined-dashboard');
      setClubs(res.data.clubs ?? []);
    } catch {
      setError('Unable to load combined dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { clubs, loading, error, reload: load };
}

export function useCombinedEvents() {
  const [events, setEvents] = useState<CombinedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ events: CombinedEvent[] }>('/members/me/combined-events');
      setEvents(res.data.events ?? []);
    } catch {
      setError('Unable to load combined events.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { events, loading, error, reload: load };
}

export function useCombinedTimetable() {
  const [entries, setEntries] = useState<CombinedTimetableEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ entries: CombinedTimetableEntry[] }>('/members/me/combined-timetable');
      setEntries(res.data.entries ?? []);
    } catch {
      setError('Unable to load combined timetable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { entries, loading, error, reload: load };
}

export function useCombinedNotices() {
  const [notices, setNotices] = useState<CombinedNotice[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ notices: CombinedNotice[]; unread_counts: Record<string, number> }>('/members/me/combined-notices');
      setNotices(res.data.notices ?? []);
      setUnreadCounts(res.data.unread_counts ?? {});
    } catch {
      setError('Unable to load combined notices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { notices, unreadCounts, loading, error, reload: load };
}
